import { create } from 'zustand';
import ServiceAgentesNocivos from '@/services/ServiceAgentesNocivos';
import { notify } from '@/stores/useToast';
import UtilsMask from '@/utils/UtilsMask';
import {
  buildRequestBody,
  buildAgentesDonut,
  buildAliquotDonut,
  type AgentesFiltros,
  type RetirementAliquot,
  type DonutVM,
} from '@/lib/agentesNocivos';

export interface CategoriaOption {
  codigo: string | number;
  descricao: string;
}

export interface AgentOption {
  label: string;
  value: string;
}

export type SortOrder = 'asc' | 'desc';

interface ExposedCollaborators {
  exposed: number;
  total: number;
  percentage: number;
}

type Any = any;

interface State {
  // filtro geral
  competency: string | null;
  cnpjs: string[];
  categories: number[];

  // filtros da tabela (aba Trabalhadores)
  nociveAgent: string[];
  specialRetirement: boolean | null;
  retirementAliquot: RetirementAliquot[];
  onlyExposedRisks: boolean;
  nameCpfMatriculation: string;

  // aba Agentes Nocivos > busca
  nociveAgentCodeDesc: string;

  // dados dos gráficos
  agentesDonut: DonutVM;
  aliquotDonut: DonutVM;
  exposedCollaborators: ExposedCollaborators;
  retirementRisksTotal: number;

  // aba Trabalhadores (retirement-risks)
  colabLista: Any[];
  colabTotal: number;
  colabPage: number;
  colabSize: number;
  colabSortBy: string | null;
  colabSortOrder: SortOrder;

  // aba Agentes Nocivos (nocive-agents)
  agLista: Any[];
  agTotal: number;
  agPage: number;
  agSize: number;
  agSortBy: string | null;
  agSortOrder: SortOrder;

  // opções
  agentOptions: AgentOption[];
  competencias: string[];
  categoriesList: CategoriaOption[];

  // loading
  loadingGraphs: boolean;
  loadingColab: boolean;
  loadingAg: boolean;
  loadingCategorias: boolean;
  loadingCompetencias: boolean;
  exportandoColab: boolean;
  exportandoAg: boolean;

  filterApplied: boolean;

  init: () => Promise<void>;
  applyFilter: () => Promise<void>;
  carregarGraficos: () => Promise<void>;
  carregarColaboradores: () => Promise<void>;
  carregarAgentes: () => Promise<void>;
  carregarAgentOptions: () => Promise<void>;
  exportarColaboradores: () => Promise<void>;
  exportarAgentes: () => Promise<void>;

  setCompetency: (v: string | null) => void;
  setCnpjs: (v: string[]) => void;
  setCategories: (v: number[]) => void;

  setNociveAgent: (v: string[]) => Promise<void>;
  setSpecialRetirement: (v: boolean | null) => Promise<void>;
  setRetirementAliquot: (v: RetirementAliquot[]) => Promise<void>;
  setOnlyExposedRisks: (v: boolean) => Promise<void>;
  searchColaborador: (term: string) => Promise<void>;
  searchAgente: (term: string) => Promise<void>;

  filterFromExposed: () => Promise<void>;
  filterFromSpecial: () => Promise<void>;

  setColabPage: (page: number) => Promise<void>;
  setColabSize: (size: number) => Promise<void>;
  setAgPage: (page: number) => Promise<void>;
  setAgSize: (size: number) => Promise<void>;
  setColabSort: (sortBy: string, sortOrder: SortOrder) => Promise<void>;
  setAgSort: (sortBy: string, sortOrder: SortOrder) => Promise<void>;
}

// corpo do filtro geral + filtros da tabela (aba Trabalhadores)
function _body(get: () => State): AgentesFiltros {
  const s = get();
  return buildRequestBody({
    competency: s.competency,
    cnpjs: s.cnpjs,
    categories: s.categories,
    nociveAgent: s.nociveAgent,
    specialRetirement: s.specialRetirement,
    retirementAliquot: s.retirementAliquot,
    onlyExposedRisks: s.onlyExposedRisks,
    nameCpfMatriculation: s.nameCpfMatriculation,
  });
}

// corpo da aba Agentes Nocivos: filtro geral + busca por código/descrição
function _agBody(get: () => State): AgentesFiltros {
  const s = get();
  return buildRequestBody({
    competency: s.competency,
    cnpjs: s.cnpjs,
    categories: s.categories,
    nociveAgentCodeDesc: s.nociveAgentCodeDesc,
  });
}

export const useAgentesNocivosStore = create<State>((set, get) => ({
  competency: null,
  cnpjs: [],
  categories: [],

  nociveAgent: [],
  specialRetirement: null,
  retirementAliquot: [],
  onlyExposedRisks: false,
  nameCpfMatriculation: '',

  nociveAgentCodeDesc: '',

  agentesDonut: buildAgentesDonut({}),
  aliquotDonut: buildAliquotDonut({}),
  exposedCollaborators: { exposed: 0, total: 0, percentage: 0 },
  retirementRisksTotal: 0,

  colabLista: [],
  colabTotal: 0,
  colabPage: 0,
  colabSize: 25,
  colabSortBy: null,
  colabSortOrder: 'asc',

  agLista: [],
  agTotal: 0,
  agPage: 0,
  agSize: 25,
  agSortBy: null,
  agSortOrder: 'asc',

  agentOptions: [],
  competencias: [],
  categoriesList: [],

  loadingGraphs: false,
  loadingColab: false,
  loadingAg: false,
  loadingCategorias: false,
  loadingCompetencias: false,
  exportandoColab: false,
  exportandoAg: false,

  filterApplied: false,

  async init() {
    set({ loadingCompetencias: true, loadingCategorias: true });
    try {
      const [competencias, categorias] = await Promise.all([
        ServiceAgentesNocivos.availableCompetencies(),
        ServiceAgentesNocivos.countCategories(),
      ]);
      const lista: string[] = competencias.data ?? [];
      set({
        competencias: lista,
        categoriesList: categorias.data ?? [],
      });
      // pré-seleciona a competência mais recente e já carrega os dados,
      // pra não depender do usuário abrir o Filtro geral manualmente
      const maisRecente = [...lista].sort().at(-1) ?? null;
      if (maisRecente) {
        set({ competency: maisRecente });
        await get().applyFilter();
      }
    } catch {
      set({ competencias: [], categoriesList: [] });
      notify.error('Erro ao carregar os filtros de agentes nocivos.');
    } finally {
      set({ loadingCompetencias: false, loadingCategorias: false });
    }
  },

  async applyFilter() {
    set({ filterApplied: true });
    await Promise.all([
      get().carregarGraficos(),
      get().carregarColaboradores(),
      get().carregarAgentes(),
      get().carregarAgentOptions(),
    ]);
  },

  async carregarGraficos() {
    set({ loadingGraphs: true });
    try {
      const { data } = await ServiceAgentesNocivos.chartsCounts(_body(get));
      set({
        agentesDonut: buildAgentesDonut(data?.nociveAgents ?? {}),
        aliquotDonut: buildAliquotDonut(data?.retirementYearsAliquot ?? {}),
        exposedCollaborators: data?.exposedCollaborators ?? { exposed: 0, total: 0, percentage: 0 },
        retirementRisksTotal: data?.retirementRisks?.total ?? 0,
      });
    } catch {
      set({
        agentesDonut: buildAgentesDonut({}),
        aliquotDonut: buildAliquotDonut({}),
        exposedCollaborators: { exposed: 0, total: 0, percentage: 0 },
        retirementRisksTotal: 0,
      });
      notify.error('Erro ao carregar os gráficos de agentes nocivos.');
    } finally {
      set({ loadingGraphs: false });
    }
  },

  async carregarColaboradores() {
    const { colabPage, colabSize, colabSortBy, colabSortOrder } = get();
    set({ loadingColab: true });
    try {
      const { data } = await ServiceAgentesNocivos.retirementRisks(
        _body(get),
        colabPage,
        colabSize,
        colabSortBy ? `${colabSortBy},${colabSortOrder}` : undefined,
      );
      set({
        colabLista: (data.content ?? []).map((r: Any) => ({
          ...r,
          cnpj: r.cnpj ? UtilsMask.cnpj(r.cnpj) : r.cnpj,
          cpf: r.cpf ? UtilsMask.cpf(r.cpf) : r.cpf,
        })),
        colabTotal: data.totalElements ?? 0,
      });
    } catch {
      set({ colabLista: [], colabTotal: 0 });
      notify.error('Erro ao carregar os trabalhadores.');
    } finally {
      set({ loadingColab: false });
    }
  },

  async carregarAgentes() {
    const { agPage, agSize, agSortBy, agSortOrder } = get();
    set({ loadingAg: true });
    try {
      const { data } = await ServiceAgentesNocivos.nociveAgents(
        _agBody(get),
        agPage,
        agSize,
        agSortBy ? `${agSortBy},${agSortOrder}` : undefined,
      );
      set({
        agLista: (data.content ?? []).map((r: Any) => ({
          ...r,
          cnpj: r.cnpj ? UtilsMask.cnpj(r.cnpj) : r.cnpj,
        })),
        agTotal: data.totalElements ?? 0,
      });
    } catch {
      set({ agLista: [], agTotal: 0 });
      notify.error('Erro ao carregar os agentes nocivos.');
    } finally {
      set({ loadingAg: false });
    }
  },

  async carregarAgentOptions() {
    try {
      const { data } = await ServiceAgentesNocivos.countCollaboratorsByAgent(_body(get));
      set({
        agentOptions: Object.keys(data ?? {}).map((k) => ({ label: k, value: k })),
      });
    } catch {
      set({ agentOptions: [] });
      notify.error('Erro ao carregar as opções de agentes nocivos.');
    }
  },

  setCompetency(v) {
    set({ competency: v });
  },

  setCnpjs(v) {
    set({ cnpjs: v });
  },

  setCategories(v) {
    set({ categories: v });
  },

  async setNociveAgent(v) {
    if (!get().competency) return;
    set({ nociveAgent: v, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async setSpecialRetirement(v) {
    if (!get().competency) return;
    set({ specialRetirement: v, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async setRetirementAliquot(v) {
    if (!get().competency) return;
    set({ retirementAliquot: v, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async setOnlyExposedRisks(v) {
    if (!get().competency) return;
    set({ onlyExposedRisks: v, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async searchColaborador(term) {
    if (!get().competency) return;
    set({ nameCpfMatriculation: term, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async searchAgente(term) {
    if (!get().competency) return;
    set({ nociveAgentCodeDesc: term, agPage: 0 });
    await get().carregarAgentes();
  },

  async filterFromExposed() {
    if (!get().competency) return;
    set({ onlyExposedRisks: true, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async filterFromSpecial() {
    if (!get().competency) return;
    set({ specialRetirement: true, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async setColabPage(page) {
    if (!get().competency) return;
    set({ colabPage: page });
    await get().carregarColaboradores();
  },

  async setColabSize(size) {
    if (!get().competency) return;
    set({ colabSize: size, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async setAgPage(page) {
    if (!get().competency) return;
    set({ agPage: page });
    await get().carregarAgentes();
  },

  async setAgSize(size) {
    if (!get().competency) return;
    set({ agSize: size, agPage: 0 });
    await get().carregarAgentes();
  },

  async setColabSort(sortBy, sortOrder) {
    if (!get().competency) return;
    // alias do backend == key da coluna (mapa identidade) -> manda a key direto.
    set({ colabSortBy: sortBy, colabSortOrder: sortOrder, colabPage: 0 });
    await get().carregarColaboradores();
  },

  async setAgSort(sortBy, sortOrder) {
    if (!get().competency) return;
    set({ agSortBy: sortBy, agSortOrder: sortOrder, agPage: 0 });
    await get().carregarAgentes();
  },

  async exportarColaboradores() {
    if (!get().competency) return;
    set({ exportandoColab: true });
    try {
      await ServiceAgentesNocivos.exportRetirementRisks(_body(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Não foi possível exportar os trabalhadores.');
    } finally {
      set({ exportandoColab: false });
    }
  },

  async exportarAgentes() {
    if (!get().competency) return;
    set({ exportandoAg: true });
    try {
      await ServiceAgentesNocivos.exportNociveAgents(_agBody(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Não foi possível exportar os agentes nocivos.');
    } finally {
      set({ exportandoAg: false });
    }
  },
}));
